import { useState, useEffect } from "react";
import {
  Input,
  Select,
  Checkbox,
  Text,
  Button,
  Flex,
  Divider,
  LabelWrapper
} from "@theorgchartdev/storybook";
import { createSelectOptions } from "@/Utils/Misc";
import { GetValuesForField } from "@/Utils/Promises";
import {
  BaseRuleSchema,
  CommonSelectProperties,
  fieldComparisons,
  mappedFieldComparisons
} from "./Constants";
import { MultiStringInput } from "./MultiStringInput";
import EditableItemGroup from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import * as React from "react";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { appstr } from "@coreapp/utils/appstr";
import {
  getFieldClassTypeFromGroup,
  removeOrganizedFieldOptionSuffix,
  getOrganizedFieldOptionValue
} from "./Utils";
import FieldTypePill from "./FieldTypePill";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { getComparisonLabel } from "@/appComponents/Blocks/Composites/RuleInput/RulePairInput/RulePairInput.util";

const LevelOptions = ["2 Levels", "3 Levels", "All Levels"];

export const RollupSettings = ({
  operator,
  fields,
  organizedFieldOptions,
  onUpdate,
  initialValues,
  dataTestIdPrefix,
  chartConfig,
  errors
}) => {
  if (!initialValues) {
    initialValues = {};
  }

  let scope = initialValues.scope ?? "2 Levels";
  let field = initialValues.field ?? "None";

  const determineFieldClassType = fieldValue => {
    if (!fieldValue || fieldValue === "None" || !organizedFieldOptions) return null;

    for (const group of organizedFieldOptions) {
      const fieldExists = group.options?.some(option => option.value === fieldValue);
      if (fieldExists) {
        return getFieldClassTypeFromGroup(group.value);
      }
    }
    return null;
  };

  let fieldClassType = initialValues.fieldClassType ?? determineFieldClassType(field);
  let includeSelf = initialValues.self ?? true;
  let rules = initialValues.rules ?? [];

  const buildDataObject = () => {
    const data = {
      type: operator,
      scope,
      field,
      self: includeSelf,
      rules
    };
    if (fieldClassType) {
      data.fieldClassType = fieldClassType;
    }
    return data;
  };

  return (
    <Flex direction={"column"} width={"100%"} gap={"12px"}>
      <Flex gap={"8px"} direction={"column"}>
        <Text size={"small"} modifier={"medium"}>
          {appstr("Scope")}
        </Text>
        <Select
          placeholder={scope}
          options={createSelectOptions(LevelOptions, value => {
            scope = value;
            onUpdate(buildDataObject());
          })}
          dataTestId={"rollup-scope-select"}
          {...CommonSelectProperties}
        />
      </Flex>
      <Flex gap={"12px"} direction={"column"}>
        {operator !== "count" && (
          <Flex gap={"8px"} direction={"column"}>
            <Text size={"small"} modifier={"medium"}>
              {appstr("Field")}
            </Text>
            <Select
              placeholder={appstr("None")}
              value={
                organizedFieldOptions && IsPositionChart(chartConfig)
                  ? getOrganizedFieldOptionValue(field, fieldClassType)
                  : field
              }
              listProps={FieldSelectListProps()}
              options={
                organizedFieldOptions && IsPositionChart(chartConfig)
                  ? organizedFieldOptions.map(group => ({
                      ...group,
                      options: group.options?.map(option => ({
                        ...option,
                        clickHandler: () => {
                          field = removeOrganizedFieldOptionSuffix(option.value);
                          fieldClassType = getFieldClassTypeFromGroup(group.value);
                          onUpdate(buildDataObject());
                        }
                      }))
                    }))
                  : createSelectOptions(
                      fields,
                      value => {
                        field = value;
                        onUpdate(buildDataObject());
                      },
                      `${dataTestIdPrefix}-field`
                    )
              }
              rightContent={
                organizedFieldOptions &&
                IsPositionChart(chartConfig) &&
                field !== "None" &&
                fieldClassType && <FieldTypePill type={fieldClassType} />
              }
              dataTestId={"rollup-field-select"}
              {...CommonSelectProperties}
            />
          </Flex>
        )}
        <Checkbox
          id="includeSelf"
          data-testid={`${dataTestIdPrefix}-include-self`}
          disabled={false}
          size="medium"
          checked={includeSelf}
          label={appstr("includeself")}
          onToggle={() => {
            includeSelf = !includeSelf;
            onUpdate(buildDataObject());
          }}
        />
        <Divider />
        <Flex gap={"12px"} direction={"column"}>
          <Text size={"small"} modifier={"bold"}>
            {appstr("Rules")}
          </Text>
          <RuleList
            fields={fields}
            organizedFieldOptions={organizedFieldOptions}
            chartConfig={chartConfig}
            onUpdate={updatedRules => {
              rules = updatedRules;
              onUpdate(buildDataObject());
            }}
            initialValues={rules}
            dataTestIdPrefix={`${dataTestIdPrefix}`}
            errors={errors}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

const RuleTypes = [
  "Equal",
  "Not Equal",
  "In",
  "Not In",
  "Greater Than",
  "Less Than",
  "Date Bracket"
];

const DurationUnits = ["Weeks", "Days", "Months"];
const DateBounds = ["Bounded", "Before", "After"];

const DefaultComparisonForType = {
  Equal: "",
  "Not Equal": "",
  In: [],
  "Not In": [],
  "Greater Than": "",
  "Less Than": "",
  "Date Bracket": "",
  equals: "None",
  notequal: "None",
  greater: "None",
  less: "None",
  greaterequal: "None",
  lessequal: "None"
};

const RollupRule = ({
  fields,
  organizedFieldOptions,
  chartConfig,
  onUpdate,
  initialValues,
  dataTestIdPrefix,
  error
}) => {
  const [fieldValues, setFieldValues] = useState([]);

  const field = React.useMemo(() => initialValues.field ?? "None", [initialValues.field]);

  const isFieldToField = React.useMemo(
    () => initialValues.isFieldToField ?? false,
    [initialValues.isFieldToField]
  );

  const comparisonField = React.useMemo(() => {
    if (initialValues.isFieldToField) {
      if (
        typeof initialValues.comparisonValue === "object" &&
        initialValues.comparisonValue?.fieldName
      ) {
        return initialValues.comparisonValue.fieldName;
      }
      if (initialValues.comparisonField) {
        return initialValues.comparisonField;
      }
      if (typeof initialValues.comparisonValue === "string") {
        return initialValues.comparisonValue;
      }
    }
    return "None";
  }, [initialValues.isFieldToField, initialValues.comparisonValue, initialValues.comparisonField]);

  const comparisonFieldClassType = React.useMemo(() => {
    if (!initialValues.isFieldToField) return null;

    if (initialValues.comparisonFieldClassType) {
      return initialValues.comparisonFieldClassType;
    }

    if (
      typeof initialValues.comparisonValue === "object" &&
      initialValues.comparisonValue?.fieldClassName
    ) {
      return initialValues.comparisonValue.fieldClassName;
    }
    const fieldToCheck =
      typeof initialValues.comparisonValue === "object"
        ? initialValues.comparisonValue.fieldName
        : initialValues.comparisonField || initialValues.comparisonValue;

    if (fieldToCheck && fieldToCheck !== "None" && organizedFieldOptions) {
      for (const group of organizedFieldOptions) {
        const fieldExists = group.options?.some(option => option.value === fieldToCheck);
        if (fieldExists) {
          return getFieldClassTypeFromGroup(group.value);
        }
      }
    }

    return null;
  }, [
    initialValues.isFieldToField,
    initialValues.comparisonFieldClassType,
    initialValues.comparisonValue,
    initialValues.comparisonField,
    organizedFieldOptions
  ]);

  const fieldClassType = React.useMemo(() => {
    if (initialValues.fieldClassType) return initialValues.fieldClassType;
    if (!field || field === "None" || !organizedFieldOptions) return null;

    for (const group of organizedFieldOptions) {
      const fieldExists = group.options?.some(option => option.value === field);
      if (fieldExists) {
        return getFieldClassTypeFromGroup(group.value);
      }
    }
    return null;
  }, [initialValues.fieldClassType, field, organizedFieldOptions]);

  const ruleType = React.useMemo(() => initialValues.ruleType ?? "Equal", [initialValues.ruleType]);

  const comparisonValue = React.useMemo(
    () =>
      initialValues.comparisonValue ?? DefaultComparisonForType[initialValues.ruleType ?? "Equal"],
    [initialValues.comparisonValue, initialValues.ruleType]
  );

  const staticRange = React.useMemo(
    () => initialValues.staticRange ?? false,
    [initialValues.staticRange]
  );

  const durationUnit = React.useMemo(
    () => initialValues.durationUnit ?? "Weeks",
    [initialValues.durationUnit]
  );

  const bounds = React.useMemo(() => initialValues.bounds ?? "Bounded", [initialValues.bounds]);

  const fieldDef = {
    GetFieldType: () => null
  };

  const onUpdateForm = update => {
    const updatedData = {
      ruleType,
      field,
      comparisonValue,
      staticRange,
      durationUnit,
      bounds,
      fieldClassType,
      isFieldToField,
      comparisonField,
      comparisonFieldClassType,
      ...update
    };

    // Rebuild proper structure if field-to-field
    if (updatedData.isFieldToField) {
      updatedData.comparisonValue = {
        fieldName: updatedData.comparisonField,
        fieldId: updatedData.comparisonField,
        fieldClassName: updatedData.comparisonFieldClassType || null
      };
    }

    onUpdate(updatedData);
  };

  useEffect(() => {
    GetValuesForField(field ?? "None").then(({ results }) => {
      setFieldValues(results);
    });
  }, [field]);

  const getComparisonOptions = () => {
    const standardComparisons = RuleTypes.map(item => ({
      label: item,
      value: item,
      dataTestId: `${dataTestIdPrefix}-rule-type-${item}`,
      clickHandler: () => {
        onUpdateForm({
          ruleType: item,
          comparisonValue: DefaultComparisonForType[item],
          isFieldToField: false
        });
      }
    }));

    if (standardComparisons.length > 0) {
      standardComparisons[standardComparisons.length - 1].divider = true;
    }

    const fieldComparisonOptions = fieldComparisons.map(item => ({
      label: getComparisonLabel(item, true, fieldDef),
      value: `field-${item}`,
      dataTestId: `${dataTestIdPrefix}-rule-type-field-${item}`,
      clickHandler: () => {
        onUpdateForm({
          ruleType: item,
          isFieldToField: true,
          comparisonField: DefaultComparisonForType[item]
        });
      }
    }));

    return [...standardComparisons, ...fieldComparisonOptions];
  };

  const multiValueRules = ["In", "Not In"];

  let submenu = null;
  if (isFieldToField) {
    submenu = (
      <Flex gap={"8px"} direction={"column"}>
        <Text size={"small"} modifier={"medium"}>
          {appstr("Field")}
        </Text>
        <Select
          value={
            organizedFieldOptions && IsPositionChart(chartConfig)
              ? getOrganizedFieldOptionValue(comparisonField, comparisonFieldClassType)
              : comparisonField
          }
          placeholder={comparisonField}
          listProps={FieldSelectListProps()}
          options={
            organizedFieldOptions && IsPositionChart(chartConfig)
              ? organizedFieldOptions.map(group => ({
                  ...group,
                  options: group.options?.map(option => ({
                    ...option,
                    clickHandler: () => {
                      const newComparisonFieldClassType = getFieldClassTypeFromGroup(group.value);
                      const fieldValue = removeOrganizedFieldOptionSuffix(option.value);
                      onUpdateForm({
                        comparisonField: fieldValue,
                        comparisonFieldClassType: newComparisonFieldClassType,
                        comparisonValue: {
                          fieldName: fieldValue,
                          fieldId: fieldValue,
                          fieldClassName: newComparisonFieldClassType
                        },
                        isFieldToField: true
                      });
                    }
                  }))
                }))
              : createSelectOptions(
                  fields,
                  value => {
                    onUpdateForm({
                      comparisonField: value,
                      comparisonFieldClassType: null,
                      comparisonValue: {
                        fieldName: value,
                        fieldId: value,
                        fieldClassName: null
                      },
                      isFieldToField: true
                    });
                  },
                  `${dataTestIdPrefix}-comparison-field`
                )
          }
          rightContent={
            organizedFieldOptions &&
            IsPositionChart(chartConfig) &&
            comparisonField !== "None" &&
            comparisonFieldClassType && <FieldTypePill type={comparisonFieldClassType} />
          }
          dataTestId={`${dataTestIdPrefix}-comparison-field-select`}
          {...CommonSelectProperties}
        />
      </Flex>
    );
  } else if (
    ruleType === "Equal" ||
    ruleType === "Not Equal" ||
    ruleType === "Greater Than" ||
    ruleType === "Less Than" ||
    ruleType === "In" ||
    ruleType === "Not In"
  ) {
    submenu = (
      <MultiStringInput
        value={comparisonValue}
        field={field}
        maxValues={multiValueRules.includes(ruleType) ? -1 : 1}
        allowFreeform={true}
        options={fieldValues}
        onUpdate={v => {
          onUpdateForm({ comparisonValue: v });
        }}
        dataTestIdPrefix={dataTestIdPrefix}
      />
    );
  } else if (ruleType === "Date Bracket") {
    submenu = (
      <Flex direction={"column"} gap={"24px"}>
        <Checkbox
          id="staticRange"
          data-testid={`${dataTestIdPrefix}-static-range`}
          disabled={false}
          size="medium"
          label={appstr("Static Range")}
          checked={staticRange}
          onToggle={() => {
            onUpdateForm({ staticRange: !staticRange });
          }}
        />
        <Flex gap={"8px"} direction={"column"}>
          <Text size={"small"} modifier={"medium"}>
            {appstr("refreshdate")}
          </Text>
          <Input size={"small"} />
          <Select
            value={durationUnit}
            placeholder={durationUnit}
            options={createSelectOptions(
              DurationUnits,
              unit => {
                onUpdateForm({ durationUnit: unit });
              },
              dataTestIdPrefix
            )}
            dataTestId={`${dataTestIdPrefix}-duration-unit`}
            {...CommonSelectProperties}
          />
        </Flex>
        <Flex gap={"8px"} direction={"column"}>
          <Text>{"Bounds"}</Text>
          <Select
            value={bounds}
            placeholder={bounds}
            options={createSelectOptions(
              DateBounds,
              value => {
                onUpdateForm({ bounds: value });
              },
              dataTestIdPrefix
            )}
            {...CommonSelectProperties}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex gap={"24px"} direction={"column"} width={"95%"}>
      <Flex direction={"column"}>
        <Select
          value={
            organizedFieldOptions && IsPositionChart(chartConfig)
              ? getOrganizedFieldOptionValue(field, fieldClassType)
              : field
          }
          placeholder={appstr("None")}
          label={appstr("Field")}
          inputOnly={false}
          require
          listProps={FieldSelectListProps()}
          message={error?.field}
          status={error?.field ? "error" : null}
          options={
            organizedFieldOptions && IsPositionChart(chartConfig)
              ? organizedFieldOptions.map(group => ({
                  ...group,
                  options: group.options?.map(option => ({
                    ...option,
                    clickHandler: () => {
                      const newFieldClassType = getFieldClassTypeFromGroup(group.value);
                      const fieldValue = removeOrganizedFieldOptionSuffix(option.value);
                      onUpdateForm({ fieldClassType: newFieldClassType, field: fieldValue });
                    }
                  }))
                }))
              : createSelectOptions(
                  fields,
                  value => {
                    onUpdateForm({ field: value });
                  },
                  `${dataTestIdPrefix}-rule-field`
                )
          }
          rightContent={
            organizedFieldOptions &&
            IsPositionChart(chartConfig) &&
            field !== "None" &&
            fieldClassType && <FieldTypePill type={fieldClassType} />
          }
          dataTestId={`${dataTestIdPrefix}-rule-field-select`}
          inputProps={{
            "data-testid": `${dataTestIdPrefix}-rule-field-input`
          }}
          {...CommonSelectProperties}
        />
        <Select
          value={isFieldToField ? `field-${ruleType}` : ruleType}
          placeholder={getComparisonLabel(ruleType, isFieldToField, fieldDef)}
          label={appstr("Comparison")}
          inputOnly={false}
          require
          options={getComparisonOptions()}
          dataTestId={`${dataTestIdPrefix}-rule-type-select`}
          {...CommonSelectProperties}
        />
      </Flex>
      {submenu}
    </Flex>
  );
};

const RuleList = ({
  fields,
  organizedFieldOptions,
  chartConfig,
  initialValues,
  onUpdate,
  dataTestIdPrefix,
  errors
}) => {
  const ruleValues = React.useMemo(() => initialValues ?? [], [initialValues]);

  const onDelete = i => {
    const updatedRuleValues = [...ruleValues];
    updatedRuleValues.splice(i, 1);
    onUpdate(updatedRuleValues);
  };

  return (
    <Flex gap={"12px"} direction={"column"}>
      <span>
        {ruleValues.map((rv, i) => {
          return (
            <Flex
              gap={"8px"}
              justifyContent={"space-between"}
              width={"100%"}
              direction={"column"}
              flex={"1"}
              key={i}
            >
              <EditableItemGroup
                centerActions={false}
                actions={[
                  {
                    dataTestId: `${dataTestIdPrefix}-rule-${i}-delete`,
                    onClick: () => onDelete(i),
                    iconName: "trash-can",
                    iconProps: { color: "error" }
                  }
                ]}
                buttonsInHierarchy
              >
                <RollupRule
                  key={`rollup-rule-${i}`}
                  dataTestIdPrefix={`${dataTestIdPrefix}-rule-${i}`}
                  fields={fields}
                  organizedFieldOptions={organizedFieldOptions}
                  chartConfig={chartConfig}
                  initialValues={rv}
                  error={errors?.[i]}
                  onUpdate={ruleSettings => {
                    const updatedRuleValues = [...ruleValues];
                    updatedRuleValues[i] = ruleSettings;
                    onUpdate(updatedRuleValues);
                  }}
                />
              </EditableItemGroup>
              <Divider />
            </Flex>
          );
        })}
      </span>
      <span>
        <Button
          onClick={() => {
            const updatedRuleValues = [...ruleValues, BaseRuleSchema];
            onUpdate(updatedRuleValues);
          }}
          size={"small"}
          modifier={"secondary"}
          className={"oc-special-secondary"}
          iconProps={{ name: "plus-solid" }}
          dataTestId={`${dataTestIdPrefix}-add-rule`}
        >
          {appstr("Create rule")}
        </Button>
      </span>
    </Flex>
  );
};
